import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { runsApi } from '@/api/workspaces'
import { runsCopy, runTraceCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { RunTrace } from '@/ui/RunTrace/RunTrace'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusBadge } from '@/ui/Status/Status'
import { formatNumber, formatUsd } from '@/utils/format'
import { formatDuration } from '@/utils/time'
import styles from './RunView.module.css'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={styles.fact}>
      <Text as="dt" tone="text-mute">
        {label}
      </Text>
      <dd className={styles.value}>{children}</dd>
    </div>
  )
}

/**
 * One run's full trace: the steps beside the run's facts.
 *
 * The detail query is keyed by workspace + run only — unlike the run *list*,
 * which varies by filter, a single run fetched by id is the same data for an
 * admin and a member, so both share one cache entry.
 */
export function RunView({
  workspaceId,
  runId,
}: {
  workspaceId: string
  runId: string
}) {
  const {
    data: run,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['run', workspaceId, runId],
    queryFn: () => runsApi.getRun(workspaceId, runId),
  })
  const showSkeleton = useSkeletonDelay(isLoading)

  if (isError && !run) {
    return (
      <RunTrace error={runsCopy.loadRunFailed} onRetry={() => void refetch()} />
    )
  }

  if (!run) {
    return showSkeleton ? <RunTrace /> : <div aria-busy="true" />
  }

  const { steps, ...summary } = run
  return (
    <Stack gap="space-16">
      {isError && (
        <Alert tone="warning">
          <Inline gap="space-8" align="baseline" wrap>
            <Text>{runsCopy.refreshRunFailed}</Text>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              {runsCopy.retry}
            </Button>
          </Inline>
        </Alert>
      )}
      {run.error_message && (
        <Alert
          tone={lifecycleTone(run.status) === 'danger' ? 'danger' : 'warning'}
        >
          {runsCopy.errorLine(run.error_kind, run.error_message)}
        </Alert>
      )}
      <div className={styles.layout}>
        <section aria-label={runsCopy.steps} className={styles.steps}>
          <RunTrace run={summary} steps={steps} />
        </section>
        <aside className={styles.aside}>
          <Card>
            <dl className={styles.facts}>
              <Fact label={runsCopy.facts.id}>
                <Text variant="code" tone="text-sub">
                  {run.id}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.status}>
                <StatusBadge tone={lifecycleTone(run.status)}>
                  {run.status}
                </StatusBadge>
              </Fact>
              <Fact label={runsCopy.facts.agent}>
                <Text variant="code" tone="text-sub">
                  {run.agent_id}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.model}>
                <Chip variant="kind" label={run.model} />
              </Fact>
              <Fact label={runsCopy.facts.provider}>
                <Text tone="text-sub">{run.provider}</Text>
              </Fact>
              <Fact label={runsCopy.facts.trigger}>
                <Text tone="text-sub">
                  {`${run.trigger_kind}${run.trigger_ref ? ` (${run.trigger_ref})` : ''}`}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.topic}>
                <Text variant="code" tone="text-sub">
                  {run.topic_id}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.llmCalls}>
                <Text tone="text-sub" numeric>
                  {run.llm_calls}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.toolCalls}>
                <Text tone="text-sub" numeric>
                  {run.tool_calls}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.tokens}>
                <Text tone="text-sub" numeric>
                  {runTraceCopy.tokens(
                    formatNumber(run.input_tokens),
                    formatNumber(run.output_tokens),
                  )}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.cost}>
                <Text tone="text-sub" numeric>
                  {formatUsd(run.est_cost_usd, 4)}
                </Text>
              </Fact>
              <Fact label={runsCopy.facts.duration}>
                <Text tone="text-sub" numeric>
                  {formatDuration(run.duration_ms)}
                </Text>
              </Fact>
            </dl>
          </Card>
        </aside>
      </div>
    </Stack>
  )
}
